<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import HeaderDetailContent from '@/components/common/header/header-detail-content.vue';
import { useInsightsAvailability } from '@/components/home/insights/use-insights-availability';
import InsightTopicsDescription from '@/components/insights/insight-topics-description.vue';
import { resolveInsightsAccess } from '@/components/insights/insights-access';
import { INSIGHTS_HEADER_ACTIONS_ID } from '@/components/insights/insights-header-actions';
import Header from '@/components/layouts/header.vue';
import { formatDate } from '@/utils/formatters';
import { Tab } from '@/utils/personalNews';
import { useTeamInfo } from '@/utils/swr';
import { featureFlags } from '@/featureFlags';

const router = useRouter();
const { data: teamInfo, state: teamInfoState } = useTeamInfo();

const access = computed(() =>
  resolveInsightsAccess(teamInfoState.value, teamInfo.value),
);

watch(
  access,
  value => {
    if (value === 'denied') router.replace({ name: 'anewsHome' });
  },
  { immediate: true },
);

const TODAY_ROUTE_NAME = 'insightsToday';

const { hasProfileLoaded, hasProfile } = useInsightsAvailability();

// 業務情報の読み込み中にタブ名を空にしないよう、判定できるまでは従来の「今日」を出す。
// タブ名は業務情報だけで決まるので、テーマ一覧など他の取得の成否には依存させない。
const showsSampleTab = computed(
  () =>
    featureFlags.ANDEV_10807_INSIGHT_SAMPLE &&
    hasProfileLoaded.value &&
    !hasProfile.value,
);

const tabs = computed<Tab[]>(() => [
  {
    name: showsSampleTab.value ? 'サンプル' : '今日',
    linkTo: { name: TODAY_ROUTE_NAME },
  },
  { name: '保存済み', linkTo: { name: 'savedInsights' } },
]);

const todayLabel = formatDate(dayjs().toISOString());
</script>

<template>
  <div v-if="access === 'allowed'" class="insights-page">
    <Header title="発想のヒント (β)" :tabs="tabs" content-width="100%">
      <template #tab="{ tab }">
        {{ tab.name }}
        <span
          v-if="tab.linkTo.name === TODAY_ROUTE_NAME && !showsSampleTab"
          class="c-title-medium--s"
          >（{{ todayLabel }}）</span
        >
      </template>
      <template #button>
        <div :id="INSIGHTS_HEADER_ACTIONS_ID" class="header-actions"></div>
      </template>
      <template #detail-content>
        <HeaderDetailContent>
          <template #detail>
            <InsightTopicsDescription class="c-text-regular--m" />
          </template>
        </HeaderDetailContent>
      </template>
    </Header>
    <div class="insights-content">
      <RouterView />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$content-max-width: 1024px;

.insights-page {
  // ヘッダー・タブ・本文で幅を揃えるため、レイアウト変数ごと上書きする。
  // 狭い画面ではホームのトップと同じく、左右 24px の余白を残して縮める。
  --layout-content-width: min(#{$content-max-width}, 100%);
}

.insights-content {
  max-width: $content-max-width;
  margin: 0 auto;
  padding: 24px 24px 0;
}

.header-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  margin-left: auto;
  white-space: nowrap;
}
</style>
